/**
 * Trade Storage Service
 * Utility functions for converting between backend and frontend trade formats
 * All trade operations should use tradeApi directly
 */

import { Trade } from "../types/trade";
import { BackendTrade } from "./tradeApi";
import { parsePositionSemantics, normalizeTradeStatus } from "../utils/positionSemantics";

const PRE_TRADE_EMOTIONS = new Set([
  "calm",
  "anxious",
  "excited",
  "fearful",
  "confident",
  "uncertain",
]);
const DURING_TRADE_EMOTIONS = new Set([
  "focused",
  "calm",
  "confident",
  "nervous",
  "stressed",
  "impatient",
  "worried",
  "relaxed",
]);
const POST_TRADE_EMOTIONS = new Set([
  "satisfied",
  "frustrated",
  "relieved",
  "regretful",
  "proud",
  "disappointed",
]);

function mapJournalEmotion<T extends string>(
  value: string | null | undefined,
  allowed: Set<string>
): T | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  return allowed.has(normalized) ? (normalized as T) : undefined;
}

/**
 * Convert quantity from broker units to standard lots based on symbol type
 * This ensures consistent display of lot sizes for all trades (new and existing)
 * @param quantity - Quantity stored in database (may be in units or lots)
 * @param symbol - Symbol (e.g., "XAU/USD", "EUR/USD")
 * @returns Quantity in standard lots
 */
const convertQuantityToLots = (quantity: number, symbol: string): number => {
  const symbolUpper = symbol.toUpperCase();
  
  // Gold (XAU): 1 standard lot = 100 oz
  // If database has quantity in ounces (e.g., 1), convert to lots (0.01)
  // If database already has lots (e.g., 0.01), check if it's < 1 to determine
  // For now, assume if quantity >= 1 and symbol is gold, it's in ounces
  if (symbolUpper.includes("XAU") || symbolUpper.includes("GOLD")) {
    // If quantity is >= 1, assume it's in ounces and convert
    // If quantity is < 1, assume it's already in lots
    if (quantity >= 1) {
      return quantity / 100;
    }
    // Already in lots, return as-is
    return quantity;
  }
  
  // For other instruments, return as-is
  return quantity;
};

/**
 * Convert backend trade format to frontend Trade format
 */
export const backendTradeToFrontendTrade = (backendTrade: BackendTrade): Trade => {
  // Some broker/demo imports may store ISO-like datetimes without a timezone
  // suffix (no trailing `Z` / offset). JavaScript then parses them as local time,
  // which shifts trades across day boundaries.
  const normalizeBackendIsoString = (value: string | null): string | null => {
    if (!value) return value;
    const s = value.trim();
    if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return s;

    // ISO datetime without timezone, e.g. 2026-03-26T12:34:56(.sss)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) {
      return `${s}Z`;
    }

    // Some backends use a space separator: 2026-03-26 12:34:56(.sss)
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) {
      return `${s.replace(" ", "T")}Z`;
    }

    return value;
  };

  // Convert entry quantity to standard lots for display
  const entryQuantityInLots = convertQuantityToLots(backendTrade.entry_quantity, backendTrade.symbol);
  const exitQuantityInLots = backendTrade.exit_quantity 
    ? convertQuantityToLots(backendTrade.exit_quantity, backendTrade.symbol)
    : undefined;

  return {
    id: backendTrade.id,
    userId: backendTrade.user_id,
    symbol: backendTrade.symbol,
    asset_class:
      typeof backendTrade.asset_class === "string" && backendTrade.asset_class.trim()
        ? backendTrade.asset_class.trim().toLowerCase()
        : undefined,
    type: backendTrade.trade_type,
    status: normalizeTradeStatus(backendTrade.status) as Trade["status"],
    entryDate: normalizeBackendIsoString(backendTrade.entry_date) ?? backendTrade.entry_date,
    entryPrice: backendTrade.entry_price,
    entryQuantity: entryQuantityInLots, // Converted to lots for display
    entryFees: backendTrade.entry_fees,
    entryValue: backendTrade.entry_value,
    exitDate: normalizeBackendIsoString(backendTrade.exit_date) ?? undefined,
    exitPrice: backendTrade.exit_price ?? undefined,
    exitQuantity: exitQuantityInLots, // Converted to lots for display
    exitFees: backendTrade.exit_fees ?? undefined,
    exitValue: backendTrade.exit_value ?? undefined,
    grossPnL: backendTrade.gross_pnl ?? undefined,
    netPnL: backendTrade.net_pnl ?? undefined,
    returnPercent: backendTrade.return_percent ?? undefined,
    roi: backendTrade.roi ?? undefined,
    strategy: backendTrade.strategy || undefined,
    notes: backendTrade.notes ?? undefined,
    preTradeEmotion: mapJournalEmotion<NonNullable<Trade["preTradeEmotion"]>>(
      backendTrade.pre_trade_emotion,
      PRE_TRADE_EMOTIONS
    ),
    duringTradeEmotion: mapJournalEmotion<NonNullable<Trade["duringTradeEmotion"]>>(
      backendTrade.during_trade_emotion,
      DURING_TRADE_EMOTIONS
    ),
    postTradeEmotion: mapJournalEmotion<NonNullable<Trade["postTradeEmotion"]>>(
      backendTrade.post_trade_emotion,
      POST_TRADE_EMOTIONS
    ),
    tags: backendTrade.tags || [],
    setupTags: backendTrade.setup_tags || [],
    mistakeTags: backendTrade.mistake_tags || [],
    attachments: [], // Backend doesn't have attachments yet
    createdAt: backendTrade.created_at,
    updatedAt: backendTrade.updated_at,
    brokerAccountCode: backendTrade.broker_account_code || undefined,
    broker_synced: backendTrade.broker_synced,
    signalEventId: backendTrade.signal_event_id ?? undefined,
    takeProfit: backendTrade.take_profit_price ?? undefined,
    stopLoss: backendTrade.stop_loss_price ?? undefined,
    lossPotential: backendTrade.loss_potential ?? undefined,
    profitPotential: backendTrade.profit_potential ?? undefined,
    positionSemantics: parsePositionSemantics(backendTrade.position_semantics),
  };
};

/**
 * Convert frontend Trade format to backend format
 */
export const frontendTradeToBackendTrade = (trade: Trade, brokerAccountCode?: string): any => {
  return {
    broker_account_code: brokerAccountCode || "",
    symbol: trade.symbol,
    trade_type: trade.type,
    status: trade.status,
    entry_date: typeof trade.entryDate === "string" ? trade.entryDate : trade.entryDate.toISOString(),
    entry_price: trade.entryPrice,
    entry_quantity: trade.entryQuantity,
    entry_fees: trade.entryFees,
    exit_date: trade.exitDate ? (typeof trade.exitDate === "string" ? trade.exitDate : trade.exitDate.toISOString()) : undefined,
    exit_price: trade.exitPrice,
    exit_quantity: trade.exitQuantity,
    exit_fees: trade.exitFees,
    strategy: trade.strategy,
    notes: trade.notes,
    tags: trade.tags || [],
    setup_tags: trade.setupTags?.length ? trade.setupTags : undefined,
    mistake_tags: trade.mistakeTags?.length ? trade.mistakeTags : undefined,
    pre_trade_emotion: trade.preTradeEmotion,
    during_trade_emotion: trade.duringTradeEmotion,
    post_trade_emotion: trade.postTradeEmotion,
    broker_trade_id: trade.id.startsWith("dx-") || trade.id.startsWith("trade-") ? trade.id : undefined,
  };
};

